// * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// * Copyright 2023 The Geek-AI Authors. All rights reserved.
// * Use of this source code is governed by a Apache-2.0 license
// * that can be found in the LICENSE file.
// * @Author yangjian102621@163.com
// * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

import axios from "axios";
import {
  getAdminToken,
  getUserToken,
  removeAdminToken,
  clearUserAuth,
} from "@/store/session";
import { useSharedStore } from "@/store/sharedata";
import pinia from "@/store/pinia";
import { router } from "@/router";

axios.defaults.timeout = 180000;
axios.defaults.baseURL = process.env.VUE_APP_API_HOST;
axios.defaults.withCredentials = true;
//axios.defaults.headers.post['Content-Type'] = 'application/json'

// HTTP拦截器
axios.interceptors.request.use(
  (config) => {
    // set token
    config.headers["Authorization"] = getUserToken();
    config.headers["Admin-Authorization"] = getAdminToken();

    // Auto inject room_id for lab/student/educator APIs.
    // This makes room context non-fragile even if some page forgets to pass ?room_id=... or params.room_id.
    try {
      const url = config.url || "";

      // Allow callers to opt out
      if (config.skipRoomId) {
        return config;
      }

      // Only inject for APIs that are expected to be room-scoped.
      const shouldTryInject =
        url.startsWith("/api/student/") || url.startsWith("/api/educator/");
      if (!shouldTryInject) {
        return config;
      }

      // Avoid injecting into these endpoints (they are not room-scoped and sometimes happen before room context exists)
      const excluded = [
        /\/api\/student\/list\/room\b/,
        /\/api\/student\/join\b/,
        /\/api\/student\/inroom\b/,
      ];
      if (excluded.some((re) => re.test(url))) {
        return config;
      }

      // If room_id already exists in URL or params, do nothing
      if (url.includes("room_id=")) {
        return config;
      }

      const store = useSharedStore(pinia);
      const roomIdFromStore = store.curRoomId || (store.curRoom && store.curRoom.id);

      // As an extra fallback, parse from current location (in case store isn't ready yet)
      const roomIdFromUrl = (() => {
        try {
          return new URLSearchParams(window.location.search).get("room_id");
        } catch (e) {
          return null;
        }
      })();

      const roomId = roomIdFromStore || roomIdFromUrl;
      if (!roomId) {
        return config;
      }

      config.params = config.params || {};
      if (config.params.room_id == null) {
        config.params.room_id = roomId;
      }
    } catch (e) {
      // Never block requests because of room injection logic.
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      const url = error.response.request.responseURL || "";
      const currentPath = window.location.pathname;
      const currentFullPath = window.location.pathname + window.location.search;
      const returnUrl = encodeURIComponent(currentFullPath);
      
      // 公开页面不自动跳转到登录页（首页、登录页等）
      const publicPages = ["/", "/login", "/student/login", "/educator/login", "/admin/login", "/register", "/student/register", "/mobile/index"];
      const isPublicPage = publicPages.includes(currentPath) || currentPath.startsWith("/login");
      
      if (url.indexOf("/api/admin") !== -1) {
        // 管理员 API 跳转到管理员登录页
        removeAdminToken();
        if (!isPublicPage && !currentPath.startsWith("/admin/login")) {
          router.push({ path: "/admin/login", query: { returnUrl } });
        }
      } else if (url.indexOf("/api/educator") !== -1) {
        // 教师 API 跳转到教师登录页
        const store = useSharedStore(pinia);
        store.clearAuth();
        if (!isPublicPage && !currentPath.startsWith("/educator/login")) {
          router.push({ path: "/educator/login", query: { returnUrl } });
        }
      } else if (url.indexOf("/api/student") !== -1) {
        // 学生 API 跳转到学生登录页
        const store = useSharedStore(pinia);
        store.clearAuth();
        if (!isPublicPage && !currentPath.startsWith("/student/login")) {
          router.push({ path: "/student/login", query: { returnUrl } });
        }
      } else {
        // 其他 API：只有在非公开页面才跳转到登录页
        const store = useSharedStore(pinia);
        store.clearAuth();
        if (!isPublicPage) {
          router.push({ path: "/login", query: { returnUrl } });
        }
      }
      error.response.data.message = "请先登录";
      return Promise.reject(error.response.data);
    }
    if (error.response && error.response.status === 403) {
      // 权限不足，跳转到403页面
      router.push("/403");
      return Promise.reject({
        message: error.response.data.message || "您没有权限访问此资源",
        code: error.response.data.code,
      });
    }
    if (error.response && error.response.status === 400) {
      return Promise.reject({
        message: error.response.data.message,
        code: error.response.data.code,
      });
    } else {
      return Promise.reject(error);
    }
  }
);

// send a http get request
export function httpGet(url, params = {}) {
  return new Promise((resolve, reject) => {
    axios
      .get(url, {
        params: params,
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

// send a http get request
export async function httpGetSync(url, params = {}) {
  const response = await axios.get(url, {
    params: params,
  });
  return response.data;
}

// send a http post request
export function httpPost(url, data = {}, options = {}) {
  return new Promise((resolve, reject) => {
    axios
      .post(url, data, options)
      .then((response) => {
        resolve(response.data);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function httpDownload(url) {
  return new Promise((resolve, reject) => {
    axios({
      method: "GET",
      url: url,
      responseType: "blob", // 将响应类型设置为 `blob`
    })
      .then((response) => {
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function httpPostDownload(url, data) {
  return new Promise((resolve, reject) => {
    axios({
      method: "POST",
      url: url,
      data: data,
      responseType: "blob", // 将响应类型设置为 `blob`
    })
      .then((response) => {
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
}
